import { Flex, Avatar, Portal, Box, useOutsideClick } from '@chakra-ui/react';
import { components, ControlProps } from 'react-select';
import { Link } from 'react-router-dom';
import { KTag } from '@/ui-library/components/atoms/KTag';
import { TruncatedTextWithTooltip } from '@/ui/TooltipText';
import { colors } from '@/theme/foundations/colors';
import { JobDetailsTable } from '@/components/global-search/search-items/JobDetailsTable';
import { useRef, useState } from 'react';

const NO_JOB_TEXT = 'No job';
const NO_EMAIL_TEXT = 'No email';

// Left Section Component
const LeftSection = ({
  image_url,
  name,
  emailText,
}: {
  image_url?: string;
  name: string;
  emailText: string;
}) => (
  <Flex gap="4px" alignItems="center" flex="1" minW="0" pr="6px">
    <Avatar
      src={image_url}
      name={name}
      size="2xs"
      borderRadius="3px"
      flexShrink={0}
    />
    <Flex gap="2px" flexDir="column" alignItems="flex-start" minW="0" flex="1">
      <TruncatedTextWithTooltip text={name} variant="heading_4" maxW="100%" />
      <TruncatedTextWithTooltip
        text={emailText}
        variant="small_text"
        maxW="100%"
        color="text-secondary"
      />
    </Flex>
  </Flex>
);

// Right Section Component
const RightSection = ({
  ats_applications,
  tagRef,
  showPopover,
  togglePopover,
  popoverRef,
}: {
  ats_applications: any[];
  tagRef: React.RefObject<HTMLDivElement>;
  showPopover: boolean;
  togglePopover: (_e: React.MouseEvent<HTMLDivElement>) => void;
  popoverRef: React.RefObject<HTMLDivElement>;
}) => {
  const isJobsPresent = ats_applications?.length > 0;
  const isMoreThanOneJob = ats_applications?.length > 1;
  const firstJob = ats_applications?.[0];

  if (!isJobsPresent) {
    return (
      <Flex
        justifyContent="flex-end"
        alignItems="center"
        pl="3px"
        flexShrink={0}
      >
        <KTag value={NO_JOB_TEXT} />
      </Flex>
    );
  }

  return (
    <Flex
      flexShrink={0}
      maxW="50%"
      minW="0"
      alignItems="center"
      gap="8px"
      pl="6px"
    >
      <Link
        to={`/jobs/${
          firstJob?.ats_job_id || firstJob?.job_id
        }?tab=candidates&appId=${firstJob?.id}`}
        style={{
          textDecoration: 'none',
          display: 'flex',
          minWidth: 0,
        }}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => {
          e?.stopPropagation();
        }}
      >
        <Flex
          alignItems="center"
          gap="6px"
          minW="0"
          _hover={{
            '& .job-link': {
              textDecoration: 'underline',
            },
          }}
        >
          <TruncatedTextWithTooltip
            className="job-link"
            variant="link"
            text={firstJob?.ats_job_name || 'Job'}
            maxW="150px"
            color="primary-500"
          />
          {/* <Icon
            as={LinkExternal02}
            color="primary-500"
            w="14px"
            h="14px"
            flexShrink={0}
          /> */}
        </Flex>
      </Link>

      {isMoreThanOneJob && (
        <Box ref={tagRef} position="relative" flexShrink={0}>
          <KTag
            variant="secondary"
            size="sm"
            value={`+${ats_applications.length - 1}`}
            cursor="pointer"
            onClick={togglePopover}
            _hover={{
              textDecoration: 'underline',
            }}
          />
          {showPopover && (
            <Portal>
              <Box
                ref={popoverRef}
                position="fixed"
                top={`${tagRef.current?.getBoundingClientRect().top || 0}px`}
                left={`${
                  (tagRef.current?.getBoundingClientRect().left || 0) - 610
                }px`}
                transform="translateY(-50%)"
                width="600px"
                maxHeight="300px"
                p="0px"
                border="1px solid"
                borderColor="secondary-100"
                backgroundColor="white"
                boxShadow="md"
                overflowY="auto"
                zIndex={9999}
                borderRadius="md"
                style={{ pointerEvents: 'auto' }}
              >
                <JobDetailsTable ats_applications={ats_applications} />
              </Box>
            </Portal>
          )}
        </Box>
      )}
    </Flex>
  );
};

export const AiScoreCandidateOption = (props: ControlProps<any, false>) => {
  const {
    image_url,
    email,
    localEmail,
    first_name,
    last_name,
    ats_applications = [],
  } = props?.data ?? ({} as any);

  const emailText = email ?? localEmail ?? NO_EMAIL_TEXT;
  const name = `${first_name ?? ''} ${last_name ?? ''}`;

  // Simplified popover state
  const [showPopover, setShowPopover] = useState(false);
  const tagRef = useRef<HTMLDivElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  useOutsideClick({
    ref: wrapperRef,
    handler: (event) => {
      // Don't close if clicking inside the popover
      if (
        popoverRef.current &&
        popoverRef.current.contains(event.target as Node)
      ) {
        return;
      }
      setShowPopover(false);
    },
  });

  const togglePopover = (e: React.MouseEvent<HTMLDivElement>) => {
    setShowPopover((prev) => !prev);
    e.stopPropagation();
  };

  return (
    <components.Option {...props}>
      <Box ref={wrapperRef}>
        <Flex
          alignItems="center"
          w="100%"
          borderBottom={`1px solid ${colors['border-color']}`}
          _last={{ borderBottom: 'none' }}
        >
          <LeftSection
            image_url={image_url}
            name={name}
            emailText={emailText}
          />
          <RightSection
            ats_applications={ats_applications}
            tagRef={tagRef}
            showPopover={showPopover}
            togglePopover={togglePopover}
            popoverRef={popoverRef}
          />
        </Flex>
      </Box>
    </components.Option>
  );
};
